import { useTranslation } from 'react-i18next'
import { Loader2, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/Pagination'
import { ContactPreviewList, type PreviewableContact } from '@/components/ContactPreviewPanel'
import type { SegmentContactSearch } from '@/hooks/useSegmentContactSearch'
import { countCustomizedContacts } from '@/lib/overrides'
import type { EnrollOverridesMap } from '@/types/api'

interface SegmentContactPickerProps {
  contactSearch: SegmentContactSearch
  overridesByContact: EnrollOverridesMap
  onSelect: (contact: PreviewableContact) => void
}

/** Trova un contatto del segmento e dice quanti sono già personalizzati (#380). */
export function SegmentContactPicker({ contactSearch, overridesByContact, onSelect }: SegmentContactPickerProps) {
  const { t } = useTranslation('sequences')
  const { searchInput, setSearchInput, page, setPage, contacts, totalPages, isFetching, isError } = contactSearch
  const searchLabel = t('enrollDialog.preview.searchContacts')

  return (
    <div className="space-y-2">
      <p className="text-sm">
        {t('enrollDialog.preview.customizedContacts', { count: countCustomizedContacts(overridesByContact) })}
      </p>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          aria-label={searchLabel}
          placeholder={searchLabel}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9 pr-9"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {isError && <p className="text-sm text-destructive">{t('enrollDialog.preview.contactsError')}</p>}
      {!isError && !isFetching && contacts.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('enrollDialog.preview.noContactsFound')}</p>
      )}
      {/* La chiave riporta in cima lo scroll interno quando cambia la pagina */}
      <ContactPreviewList
        key={page}
        contacts={contacts}
        overridesByContact={overridesByContact}
        onSelect={onSelect}
      />
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  )
}
